/* eslint-disable no-magic-numbers */
import { Stripe } from '@stripe/stripe-js';

import { ICreatePremiumResponse, PremiumPurchaseStatus } from '../../api/rest/createPremium';
import { UNIVERSAL_ENV_VARS } from '@config/environmentVariables';
import { CURRENT_PLANS, IPaymentPlan, PlanDuration, PREMIUM_PLANS } from '@domain/premium/plans';
import { ICoupon } from '@app-types/stripe';

import { centsToDollars } from '@lib/currency/centsToDollars';
import { dollarsToCents } from '@lib/currency/dollarsToCents';
import { percentOfCurrency } from '@lib/currency/percentOfCurrency';
import { reportMykissClientError } from '@lib/errors/client';
import { ErrorMessage } from '@lib/forms/ErrorMessage';

const ONE_HUNDRED = 100;

export const { STRIPE_KEY } = UNIVERSAL_ENV_VARS;

export const durationMap = {
  '1': PlanDuration.MONTHLY,
  '12': PlanDuration.YEARLY,
};

export const getDiscountPrice = (coupon: ICoupon): number => {
  const basePayment = dollarsToCents(coupon.group.stripe_plan.price);
  return percentOfCurrency(basePayment, ONE_HUNDRED - parseFloat(coupon.group.percent_off));
};

export const formatBillingFrequency = (coupon: ICoupon): string | undefined => {
  const offerDurationInMonths = coupon.group.duration_in_months;
  const planDurationString = coupon.group.stripe_plan.duration_in_months.toString();
  const planDuration = (durationMap as Record<string, PlanDuration>)[planDurationString];

  const offerDurationInYears = Math.ceil(offerDurationInMonths / 12);

  if (planDuration === PlanDuration.YEARLY && offerDurationInYears > 1) {
    return '/year';
  }
  if (planDuration === PlanDuration.MONTHLY && offerDurationInMonths > 1) {
    return '/month';
  }
  return '';
};

export const formatOfferDuration = (coupon: ICoupon): string | undefined => {
  const offerDurationInMonths = coupon.group.duration_in_months;
  const planDurationString = coupon.group.stripe_plan.duration_in_months.toString();
  const planDuration = (durationMap as Record<string, PlanDuration>)[planDurationString];

  const offerDurationInYears = Math.ceil(offerDurationInMonths / 12);

  if (planDuration === PlanDuration.YEARLY && offerDurationInMonths % 12 !== 0) {
    reportMykissClientError(
      new Error(`Incorrect coupon offer period: ${offerDurationInMonths}`),
      'Coupon use',
      'warning',
    );
  }

  if (planDuration === PlanDuration.YEARLY && offerDurationInYears > 0) {
    return offerDurationInYears > 1 ? `${offerDurationInYears} years` : '12 months';
  }
  return `${offerDurationInMonths} month${offerDurationInMonths > 1 ? 's' : ''}`;
};

const handleSetupIntent = async (
  setupIntentSecret: string,
  stripeInstance: Stripe,
): Promise<{ success: boolean; errorMessages: ErrorMessage[] }> => {
  const response = await stripeInstance.confirmCardSetup(setupIntentSecret);

  if (response.setupIntent?.status === 'succeeded') {
    return { success: true, errorMessages: [] };
  }
  if (response.error) {
    return { success: false, errorMessages: [{ errorMessage: response.error.message ?? '' }] };
  }
  reportMykissClientError(new Error('Unknown response from Stripe setup intent confirmation'));
  return { success: false, errorMessages: [] };
};

const handlePaymentIntent = async (
  paymentIntentSecret: string,
  stripeInstance: Stripe,
): Promise<{ success: boolean; errorMessages: ErrorMessage[] }> => {
  const response = await stripeInstance.confirmCardPayment(paymentIntentSecret);

  if (response.paymentIntent?.status === 'succeeded') {
    return { success: true, errorMessages: [] };
  }
  if (response.error) {
    return { success: false, errorMessages: [{ errorMessage: response.error.message ?? '' }] };
  }
  reportMykissClientError(new Error('Unknown response from Stripe payment intent confirmation'));
  return { success: false, errorMessages: [] };
};

// This can be used to process the result of a call to POST /me/web_subscriptions in order to
// handle any intents needed to complete the purchase.
export const handlePurchaseResponse = async (
  premiumCreationResponse: ICreatePremiumResponse,
  stripe: Stripe,
): Promise<{ success: boolean; errorMessages: ErrorMessage[] }> => {
  const { status, setupIntentSecret, paymentIntentSecret } = premiumCreationResponse;
  if (status === PremiumPurchaseStatus.Incomplete && paymentIntentSecret) {
    return handlePaymentIntent(paymentIntentSecret, stripe);
  }
  if (status === PremiumPurchaseStatus.Complete && setupIntentSecret) {
    return handleSetupIntent(setupIntentSecret, stripe);
  }
  if (status === PremiumPurchaseStatus.Complete) {
    return Promise.resolve({ success: true, errorMessages: [] });
  }
  reportMykissClientError(new Error('Unexpected response for subscription creation'));
  return Promise.resolve({
    success: false,
    errorMessages: [
      {
        errorMessage:
          'An error occurred on our server. Please contact support if the problem persists.',
      },
    ],
  });
};

export const getTrialString = (trialDays: number | undefined): string => {
  if (!trialDays) {
    return '';
  }
  const daysInMonth = 30;
  if (trialDays >= daysInMonth) {
    const trialMonths = Math.round(trialDays / daysInMonth);
    return `(after ${trialMonths} month trial)`;
  }
  return `(after ${trialDays} day trial)`;
};

export const getTrialButtonString = (trialDays: number): string => {
  const daysInMonth = 30;
  if (trialDays >= daysInMonth) {
    const trialMonths = Math.round(trialDays / daysInMonth);
    return `Start ${trialMonths} month free trial`;
  }
  return `Start ${trialDays} day free trial`;
};

export const getCouponDescription = (coupon: ICoupon): string => {
  const couponGroup = coupon.group;

  if (couponGroup.type_name === 'gift') {
    return `Receive Fishbrain Pro for ${couponGroup.duration_in_months} month(s).`;
  }
  const price = getDiscountPrice(coupon);
  const priceLabel = price === 0 ? 'nothing' : centsToDollars(price);
  return `Pay ${priceLabel}${formatBillingFrequency(coupon)} for ${formatOfferDuration(
    coupon,
  )}. After that you will pay $${couponGroup.stripe_plan.price} every ${
    couponGroup.stripe_plan.duration_in_months
  } month(s).`;
};

export function isCouponPlanValid(coupon: ICoupon): boolean {
  return PREMIUM_PLANS.some(plan => plan.planID === coupon.group.stripe_plan.external_id);
}

export function isCouponExpired(coupon: ICoupon): boolean {
  return new Date(coupon.group.expires_at) < new Date();
}

export function findMatchingPlan(coupon: ICoupon): IPaymentPlan | undefined {
  const planDurationString = coupon.group.stripe_plan.duration_in_months.toString();
  const planDuration = (durationMap as Record<string, PlanDuration>)[planDurationString];
  return CURRENT_PLANS.find(plan => plan.planDuration === planDuration);
}
